/* eslint-disable */
import type { Personalization } from "../../cash/mosaic/personalization/api/v1/personalization.pb";
import type { Color } from "../../cash/ui/Color.pb";
import type { Image } from "../../cash/ui/Image.pb";
import type { Money } from "../../common/currency.pb";
import type { RecurringScheduleFrequency } from "../../repeatedly/common.pb";
import type { ClientScenario } from "../api.pb";
import type { Orientation } from "../orientation.pb";
import type { PaymentCreationMechanism } from "../payment.pb";
import type { RatePlan } from "../rate_plan.pb";
import type { Reaction } from "../reaction.pb";
import type { Region } from "../region.pb";
import type { Role } from "../role.pb";
import type { ScenarioPlan } from "../scenarios.pb";
import type { Avatar } from "../ui.pb";
import type { Timeline } from "./timeline.pb";

/** Reasons that a payment sender may request a refund from a recipient. */
export enum RequestRefundReason {
  /** OTHER - `OTHER` is here as a fallback but is not intended for use. */
  OTHER = "OTHER",
  SENDER_NOT_AUTHORIZED = "SENDER_NOT_AUTHORIZED",
  WRONG_RECIPIENT = "WRONG_RECIPIENT",
  PURCHASE_CANCELLED = "PURCHASE_CANCELLED",
  PURCHASE_RETURNED = "PURCHASE_RETURNED",
  DID_NOT_RECEIVE = "DID_NOT_RECEIVE",
  NOT_AS_DESCRIBED = "NOT_AS_DESCRIBED",
  SCAM_REPORT = "SCAM_REPORT",
}

/** For the benefit of figuring out which flow this payment belongs to. */
export enum ScenarioPlanSource {
  NONE = "NONE",
  P2P = "P2P",
}

/** Rollup types for payments that can be rolled-up together into a single row. */
export enum RollupType {
  REFERRAL = "REFERRAL",
  /** INVESTMENT_ORDER - Includes all standard, custom, and recurring investment orders. */
  INVESTMENT_ORDER = "INVESTMENT_ORDER",
  CARD_TRANSACTION = "CARD_TRANSACTION",
  AUTH_TRANSACTION = "AUTH_TRANSACTION",
}

export enum InvestmentOrderType {
  STANDARD_ORDER = "STANDARD_ORDER",
  /** AUTO_INVEST - Recurring orders. */
  AUTO_INVEST = "AUTO_INVEST",
  CUSTOM_ORDER = "CUSTOM_ORDER",
  ROUNDUP_ORDER = "ROUNDUP_ORDER",
}

export enum PaymentState {
  /**
   * PAYMENT_NOT_KNOWN - The server has not seen a payment with the requested app token and payment token. It's
   * possible that it simply hasn't arrived yet, or its possible that its never arriving. For
   * example, the user might have deleted the payment token from the email before sending.
   */
  PAYMENT_NOT_KNOWN = "PAYMENT_NOT_KNOWN",
  SCHEDULED = "SCHEDULED",
  WAITING_ON_SENDER = "WAITING_ON_SENDER",
  WAITING_ON_RECIPIENT = "WAITING_ON_RECIPIENT",
  PROCESSING = "PROCESSING",
  COMPLETE = "COMPLETE",
  WAITING_ON_RISK = "WAITING_ON_RISK",
  RECIPIENT_EMAIL_BOUNCED = "RECIPIENT_EMAIL_BOUNCED",
  RECIPIENT_REJECTED = "RECIPIENT_REJECTED",
  SENDER_CANCELED = "SENDER_CANCELED",
  EXPIRED = "EXPIRED",
  BLOCKED_BY_RISK = "BLOCKED_BY_RISK",
  FAILED = "FAILED",
  /** WAITING_ON_EXTERNAL - Payment is waiting on some external system (eg the bitcoin blockchain) to confirm the payment. */
  WAITING_ON_EXTERNAL = "WAITING_ON_EXTERNAL",
}

export enum Step {
  /** CARD - Sender or recipient is required to link a debit card or bank account. */
  CARD = "CARD",
  /** RELINK_CARD_OR_RETRY - Sender card failed to authorize. Allow to link a new card or retry the same card. */
  RELINK_CARD_OR_RETRY = "RELINK_CARD_OR_RETRY",
  /** FACEBOOK_OR_TLO - Customer is required to provide either Facebook or TLO information to upgrade. */
  FACEBOOK_OR_TLO = "FACEBOOK_OR_TLO",
  /** SSN_AND_BIRTH_DATE - Customer needs to provide SSN last four and maybe their birth date to upgrade. */
  SSN_AND_BIRTH_DATE = "SSN_AND_BIRTH_DATE",
  /** TLO - Customer is required to provide TLO information to upgrade. */
  TLO = "TLO",
  /** USAGE_REASON - Customer is required to provide the reason for using Cash App to upgrade. */
  USAGE_REASON = "USAGE_REASON",
  /** RATE_PLAN - Customer is required to select a rate plan. */
  RATE_PLAN = "RATE_PLAN",
  /** SMS - Sender is required to provide an SMS number for SMS notifications. */
  SMS = "SMS",
  /** SMS_VERIFICATION - Sender is required to provide the SMS verification code. */
  SMS_VERIFICATION = "SMS_VERIFICATION",
  /** CAMPAIGN - Recipient is a candidate in a political campaign (accepting donations). */
  CAMPAIGN = "CAMPAIGN",
  /** IOS_DONATION - Payment is a donation to a non-profit that was initiated in the iOS app. */
  IOS_DONATION = "IOS_DONATION",
  /** STATUS - The customer has no action to take on this payment. */
  STATUS = "STATUS",
  /** FAILURE - Unused. */
  FAILURE = "FAILURE",
  /** CONFIRM_OR_DENY - The sender is required to confirm a payment potentially spoofed or duplicated. */
  CONFIRM_OR_DENY = "CONFIRM_OR_DENY",
  /** PASSCODE_CONFIRMATION - Sender needs to confirm using his PASSCODE. */
  PASSCODE_CONFIRMATION = "PASSCODE_CONFIRMATION",
  /** PASSCODE_SMART_CARD_LINK - Sender can link a card shared by another email address of the app by providing its CVV. */
  PASSCODE_SMART_CARD_LINK = "PASSCODE_SMART_CARD_LINK",
  /** EMAIL_ADDRESS - In oauth flow, customer begins by identifying who they are. */
  EMAIL_ADDRESS = "EMAIL_ADDRESS",
}

/** A client's view of a payment. */
export interface UiPayment {
  /** Either the sender token or recipient token, depending on the role. */
  token?: string;
  orientation?: Orientation;
  role?: Role;
  sender_id?: string;
  recipient_id?: string;
  state?: PaymentState;
  amount?: Money;
  /** The boost amount, if applicable. Only populated for boosted transactions. */
  boost_amount?: Money;
  /** Amount that the sender pays including fees. Only populated for the sender. */
  sender_amount?: Money;
  /** Amount that the recipient receives after deducting fees. Only populated for the recipient. */
  recipient_amount?: Money;
  /** All dates are in milliseconds since 1/1/1970 UTC. */
  created_at?: number;
  updated_at?: number;
  scheduled_for?: number;
  captured_at?: number;
  refunded_at?: number;
  paid_out_at?: number;
  /**
   * Used in combination with the payment state to determine which actions can be taken.
   *
   * @deprecated
   */
  steps?: Step[];
  /** Deprecated in version 2.9 in favor of render_data. */
  history_data?: PaymentHistoryData;
  /**
   * An opaque string to the client. The render data is passed into javascript when generating the
   * payment history data client-side.
   */
  render_data?: string;
  /** Initiator's note, such as "dinner on wednesday". */
  note?: string;
  /** When displaying or sorting payments by date, use the display date. Added for v2.11. */
  display_date?: number;
  /**
   * Apps should treat a payment as outstanding if outstanding_until is in the future. A null
   * value should be treated like the payment is not outstanding (equivalent to 0). Added for v2.14.
   */
  outstanding_until?: number;
  /** This entity should not be shown in the activity list until this date if specified. */
  hidden_until?: number;
  /** The external ID of the payment. Only populated for the initiator. */
  external_id?: string;
  /** The url to fetch to get receipt items in a JSON format */
  itemized_receipt_url?: string;
  /** The associated payment schedule token, if this payment was created through payment-scheduler. */
  payment_schedule_token?: string;
  /** Used to populate plasma related fields in `render_data`. */
  scenario_plan_source?: ScenarioPlanSource;
  /** URL for image to overlay on avatar. */
  avatar_overlay_image?: string;
  /** Size of the avatar overlay. * */
  avatar_overlay_size?: number;
  /**
   * Rollup type for this payment. If set, payments with the same rollup_type will be rolled-up into
   * a single row in the activity list.
   */
  rollup_type?: RollupType;
  /**
   * Not to be used by client.
   * Region for the payment given a role.
   */
  customer_region?: Region;
  /**
   * Not to be used by client.
   * Region for the payment given a role.
   */
  creation_mechanism?: PaymentCreationMechanism;
  /** Payload data associated to `rollup_type`. */
  rollup_data?: RollupData;
  /**
   * Type of payment. If this is set and specified in
   * PaymentHistoryConfig.top_level_activity_payment_type_deny_list, we will hide the sync
   * entity from the top-level activity.
   */
  payment_type?: string;
  /** If the asset of the payment is a stock, the stock's token will be set in this field. */
  gifted_investment_entity_token?: string;
  /**
   * If this payment is part of a Buy Now Pay Later program, then this will be
   * populated with the loan token for the associated sync entity.
   */
  lending_loan_token?: string;
  /** hack week. This is for threaded reactions in a payment. */
  threaded_payment_replies?: ThreadedPaymentReply[];
  /**
   * Link any other associated payment. This will be used in Rounds Up to link a card transaction to
   * the associated investment triggered by it. Multiple card transaction can link to the same payment
   * token.
   */
  associated_payment_token?: string;
}

/**
 * `RollupType` was created as an enum type. Use `RollupData` to provide payload data when
 * necessary to help clients build up the corresponding rollup view.
 */
export interface RollupData {
  /**
   * Defines an order's type. Use this to organise investment orders by sections into the investment
   * order rolled view. Clients should expect this field to be set only if the payment is an
   * investment order.
   */
  investment_order_type?: InvestmentOrderType | undefined;
}

/** Data used to compose the client's view of this payment. */
export interface PaymentHistoryData {
  /** The title in the flat activity list (2.14+) or in the bubble for prior releases. */
  title?: string;
  /** Shown below the title in the flat activity list. Added for 2.14. */
  subtitle?: string;
  /** Shown in support flows. Added for 2.34. */
  support_title?: string;
  /** Shown in support flows where a short string is required. Added for 2.36. */
  support_short_title?: string;
  /** Shown in support flows. Added for 2.36. */
  support_subtitle?: string;
  /** The accessibility title representing the payment cell in support flows. */
  support_accessibility_label?: string;
  /** The title in the threaded list. Added for 2.14. */
  threaded_title?: string;
  /** Shown below the title in the threaded list. Added for 2.14. */
  threaded_subtitle?: string;
  /** Shown below the amount in the threaded list. */
  threaded_display_date?: string;
  /**
   * Setting this will override the button 'inline_button' in the threaded view. if
   * threaded_button_override.hide value is set then the whole button will be hidden, otherwise the
   * threaded_button_override.button will be used
   */
  threaded_button_override?: PaymentHistoryDataThreadedButtonOverride;
  /**
   * Shown on the web deposit page, and in receipt page Open Graph meta data.
   * Describes the payment in sentence form. Is visible in SMS message previews and social media.
   */
  preview_title?: string;
  short_description?: string;
  long_description?: string;
  /**
   * This field is no longer used and should always be null.
   *
   * @deprecated
   */
  support_description?: string;
  square_message?: string;
  is_action_required?: boolean;
  is_outstanding_request?: boolean;
  notes?: string;
  primary_button?: PaymentHistoryButton;
  secondary_button?: PaymentHistoryButton;
  /**
   * URL route that clients should open when interacting with this item in the activity feed.
   * When not present, clients will open the receipt associated with this item.
   */
  url_to_open?: string;
  /**
   * URL route that clients should open when interacting with the avatar portion
   * of this item in the activity feed. When not present, clients will open either the
   * url associated with the item (`url_to_open`) or the receipt associated with this item
   * if a URL is not provided.
   */
  avatar_url_to_open?: string;
  /**
   * Additional buttons that get displayed as links. Includes "Right to Refund", etc.
   * Cash 2.0 clients only support OPEN_URL button actions.
   */
  additional_buttons?: PaymentHistoryButton[];
  /** Additional buttons that get displayed in an overflow ("...") menu. Added for 2.14. */
  overflow_buttons?: PaymentHistoryButton[];
  /**
   * Inline button is shown in the flat activity list. Typically it is the same as the
   * primary button, though it might have shorter text. Added for 2.14.
   */
  inline_button?: PaymentHistoryButton;
  inline_button_treatment?: PaymentHistoryDataInlineButtonTreatment;
  /** The title of the response bubble when complete. Only applies to requests. */
  response_title?: string;
  /** Some states (currently just CA), require emailing receipts so that customers can print them. */
  is_email_receipt_required?: boolean;
  /**
   * ScenarioPlan contains info about additional steps to complete the payment. Only applies to
   * pending payments.
   */
  scenario_plan?: ScenarioPlan;
  scenario_plan_map?: ScenarioPlanMap;
  rate_plan?: RatePlan;
  /**
   * In the payment list of v2.9+, payments are sorted in descending order of their sort_priority.
   * Deprecated for the apps in v2.11 in favor of UiPayment.sort_priority.
   */
  sort_priority?: number;
  transaction_id?: string;
  status_text?: string;
  detail_view_status_title?: string;
  detail_view_status_subtitle?: string;
  footer_text?: string;
  headline?: string;
  /** Shown at the top of the details view/receipt. e.g. "Payment from {{other}}" */
  header_text?: string;
  display_instrument?: string;
  activity_section?: PaymentHistoryDataActivitySection;
  callout_detail_item?: PaymentHistoryDataCalloutDetailItem;
  detail_rows?: PaymentHistoryDataDetailRow[];
  /** Phrases replace label/value detail rows. */
  detail_row_phrases?: string[];
  status_treatment?: PaymentHistoryDataStatusTreatment;
  /**
   * The text to display to confirm that a customer wants to cancel this payment.
   * Null if the payment in not cancellable.
   */
  confirm_cancellation_text?: string;
  /** Icon displayed to the left of the title in the flat activity list. Added for 2.14. */
  title_icon?: PaymentHistoryDataIcon;
  /** Icon displayed to the left of the subtitle in the flat activity list. Added for 2.14. */
  subtitle_icon?: PaymentHistoryDataIcon;
  /** Icon displayed to the left of the subtitle in the threaded list. Added for 3.46. */
  threaded_subtitle_icon?: PaymentHistoryDataIcon;
  amount_treatment?: PaymentHistoryDataAmountTreatment;
  amount_treatment_activity_list?: PaymentHistoryDataAmountTreatment;
  /** The formatted amount to be displayed in the activity list. */
  amount_formatted_activity_list?: string;
  /** The formatted amount to be displayed in a receipt. */
  amount_formatted?: string;
  details_view_content?: PaymentHistoryDataDetailsViewContent;
  /**
   * Shown at the top of the details view, below the header_text.
   * Examples: "Payment to $dan", "netflix.com", "San Francisco, CA", "From Chase Bank",
   * "ATM in Nashville, TN".
   */
  header_subtext?: string;
  /** Text shown below the header icon in the "more info" sheet. */
  more_info_sheet_status_text?: string;
  more_info_sheet_status_subtext?: string;
  more_info_sheet_header_icon?: PaymentHistoryDataMoreInfoSheetHeaderIcon;
  more_info_sheet_header_icon_treatment?: PaymentHistoryDataUiStatusTreatment;
  /** The reactions available to the reactions picker dialog. */
  reactions?: PaymentHistoryReactions;
  /** Render data for drawing an avatar. */
  avatar?: Avatar;
  blockchain_transaction_id?: string;
  is_bitcoin_transaction?: boolean;
  /** Encapsulate content in the new support options sheet. Introduced in v3.13 of the app. */
  support_options?: PaymentHistoryDataSupportOptions;
  /**
   * An icon to overlay on the bottom right of the customer avatar.
   * Note: Clients will crash if both `avatar_overlay_icon` and `avatar_overlay_icon_url` are set.
   */
  avatar_overlay_icon?: PaymentHistoryDataOverlayIcon;
  /**
   * A remote icon which will be displayed along side the customer avatar. Both the customer avatar
   * and the `avatar_overlay_icon_url` will be resized to about 2 thirds of their shared container
   * and laid out so that the customer avatar is at the back top left corner, and the remote icon at
   * the front bottom right corner.
   * Note: Clients will crash if both `avatar_overlay_icon` and `avatar_overlay_icon_url` are set.
   */
  avatar_overlay_icon_url?: string;
  /** A remote icon which will be displayed along side the customer avatar in dark mode. */
  dark_theme_avatar_overlay_icon_url?: string;
  /**
   * If set, it overrides accentColor as the backgroundColor of the overlayIcon (badge).
   * Does not impact the background color of the main icon or background color of avatar_overlay_icon_url.
   * Added in 3.46
   */
  overlay_icon_background_color?: Color;
  info_module?: PaymentHistoryDataInfoModule;
  /**
   * Specify shape of the icon to overlay on the bottom right of the customer avatar.
   * Added in 3.56
   */
  icon_overlay_shape?: PaymentHistoryDataIconOverlayShape;
  timeline?: Timeline;
  /** Shows cascading avatars on the left-hand side of the Activity row. */
  stacked_avatars?: PaymentHistoryDataStackedAvatars;
  /**
   * Shows a chevron implying a page navigation when row is clicked.
   * Amount or inline button will not show when this is enabled.
   */
  show_chevron?: boolean;
  /** The text color for the main subtitle. Uses default color if set to null. Added in 3.64. */
  subtitle_color?: Color;
  /**
   * Overrides some attributes on the PaymentHistoryData when is displayed on the
   * context of associated payments.
   */
  associated_payment_options?: PaymentHistoryDataAssociatedPaymentsOverride;
  /** Overrides default Avatar/logo shown in the threaded list. */
  threaded_avatar_url?: string;
  /** Personalization on the entity */
  personalization?: Personalization;
}

export enum PaymentHistoryDataInlineButtonTreatment {
  /**
   * PRIMARY - The button is used for a single required action to complete a task, such as paying a bill.
   * It is rendered as white text on a bold green background.
   */
  PRIMARY = "PRIMARY",
  /**
   * ALTERNATE - The button is used to initiate one or more required actions to advance a task, such as
   * accepting a payment. It is rendered as green text on a white background with a green border.
   */
  ALTERNATE = "ALTERNATE",
  /**
   * OPTIONAL - The button is used to initiate an optional action on an unblocked task, such as adding a
   * reaction to a payment. It is rendered as black text on a white background with a grey border.
   */
  OPTIONAL = "OPTIONAL",
}

export enum PaymentHistoryDataActivitySection {
  OUTSTANDING = "OUTSTANDING",
  COMPLETE = "COMPLETE",
}

export enum PaymentHistoryDataStatusTreatment {
  /** NORMAL - Display the long description in grey. */
  NORMAL = "NORMAL",
  /** SUCCESS - Display the long description in green with a checkmark. */
  SUCCESS = "SUCCESS",
  /** FAILURE - Display the long description with an X. */
  FAILURE = "FAILURE",
}

/** Icons shown in the list view. Added for 2.14. */
export enum PaymentHistoryDataIcon {
  /** BUSINESS - Looks like two buildings. */
  BUSINESS = "BUSINESS",
  /** NOTE - Looks like an arrow pointing down then right. */
  NOTE = "NOTE",
  /** CARD - Looks like a debit/credit card. */
  CARD = "CARD",
  /** ONLINE - Looks like a globe. */
  ONLINE = "ONLINE",
  /** NO_SYMBOL - Looks like a circle with a diagonal line through it. */
  NO_SYMBOL = "NO_SYMBOL",
  /** ALERT - Looks like a gray-outlined triangle with a "!" inside. */
  ALERT = "ALERT",
  /** RETURNED - Looks like a semi-circular arrow that reverses back to the left. */
  RETURNED = "RETURNED",
  /** RECURRING - Looks like an arrow that loops around and points at its tail. */
  RECURRING = "RECURRING",
  /** DIRECT_DEPOSIT - Looks like an arrow pointing down into a tray. */
  DIRECT_DEPOSIT = "DIRECT_DEPOSIT",
  /** DIRECT_DEBIT - Looks like an arrow pointing up out of a tray. */
  DIRECT_DEBIT = "DIRECT_DEBIT",
  /** SHIELD - Looks like a shield with a check mark inside. */
  SHIELD = "SHIELD",
  /** INVESTING - Looks like chart increasing emoji with an arrow at the end. */
  INVESTING = "INVESTING",
  /** CLOCK - Looks like a clock. */
  CLOCK = "CLOCK",
  /** VERIFIED - Looks like a circle with a check mark inside. */
  VERIFIED = "VERIFIED",
  /** DOUBLE_CHEVRON - >> Icon. Used for scheduled payments that have been skipped. */
  DOUBLE_CHEVRON = "DOUBLE_CHEVRON",
  /** ALERT_PROMINENT - Looks like a red-filled shield with a white "!" inside. Introduced in 3.20. Updated in 3.30 */
  ALERT_PROMINENT = "ALERT_PROMINENT",
  /** QR_CODE - Looks like a QR code. Introduced in 3.23. */
  QR_CODE = "QR_CODE",
  /** SHIELD_CHECKMARK - Looks like a green-filled shield with a white checkmark inside. Introduced in 3.30 */
  SHIELD_CHECKMARK = "SHIELD_CHECKMARK",
  /** IN_PROGRESS - Circle with three horizontal dots in the middle. Used for pending and similar cases. */
  IN_PROGRESS = "IN_PROGRESS",
  /** PAY_WITH_CASH - Looks like a scan icon with the Cash App logo in the middle. Introduced in 3.36. */
  PAY_WITH_CASH = "PAY_WITH_CASH",
  /** LOGOMARK - Cash App logo. */
  LOGOMARK = "LOGOMARK",
  /**
   * BORROW - Borrow logo. Looks like an arrow that curves up and to the right. Introduced in 3.41.
   * Supported in InfoModule.
   */
  BORROW = "BORROW",
  /** TRANSFER - Looks like ⇄ (U+21C4). Introduced in 3.76. */
  TRANSFER = "TRANSFER",
  /** GROUP - Group logo. Looks like stacked placeholder avatars. Introduced in TBD. */
  GROUP = "GROUP",
  /**
   * NOTICE - Looks like a gray-outlined circle with a "!" inside. Introduced in 3.77.
   * Activity feed fallback is ALERT.
   */
  NOTICE = "NOTICE",
  /**
   * NOTICE_PROMINENT - Looks like a red-filled circle with a "!" inside. Introduced in 3.77.
   * InfoModule fallback is ALERT_PROMINENT.
   */
  NOTICE_PROMINENT = "NOTICE_PROMINENT",
  /**
   * BLOCKED_PROMINENT - Looks like a red-filled circle with a "-" inside. Introduced in 3.77.
   * InfoModule fallback is ALERT_PROMINENT.
   */
  BLOCKED_PROMINENT = "BLOCKED_PROMINENT",
  /** PAYMENT_PLAN - Looks like one two branches. Introduced in 3.91. */
  PAYMENT_PLAN = "PAYMENT_PLAN",
}

/** Treatment for the amount in the "phrases" detail view */
export enum PaymentHistoryDataAmountTreatment {
  /** STANDARD - Display the amount in black. */
  STANDARD = "STANDARD",
  /** FADED - Display the amount in a faded black. */
  FADED = "FADED",
  /** STRIKETHROUGH - Display the amount in a faded black with strikethrough. */
  STRIKETHROUGH = "STRIKETHROUGH",
}

/** Icon to display in the header of the "more info" sheet. */
export enum PaymentHistoryDataMoreInfoSheetHeaderIcon {
  /** CHECKMARK - Checkmark icon. Will be tinted with remote UiCustomer.accent_color if non-null. */
  CHECKMARK = "CHECKMARK",
  /** ERROR - Circle with a horizontal dash in the middle. Used for declined and similar cases. */
  ERROR = "ERROR",
  /** INSTANT - Instant lightning bolt icon. */
  INSTANT = "INSTANT",
  /** WAITING - Circle with three horizontal dots in the middle. Used for pending and similar cases. */
  WAITING = "WAITING",
  /**
   * REPORTED - Shield with an exclamation mark in the middle. Deprecated in 3.32
   *
   * @deprecated
   */
  REPORTED = "REPORTED",
  /** SECURITY_SHIELD - Shield with a check mark in the middle. */
  SECURITY_SHIELD = "SECURITY_SHIELD",
  /** SKIPPED - >> Icon. Used for scheduled payments that have been skipped. */
  SKIPPED = "SKIPPED",
  /** BLOCKED - A circle with a diagonal line through it. Used for scheduled payments that have been missed. */
  BLOCKED = "BLOCKED",
  /**
   * ALERT_FILLED - A red-filled shield with a white "!" in the middle. Should be used with `UiStatusTreatment.NONE`.
   * Introduced in 3.20. Updated in 3.30
   */
  ALERT_FILLED = "ALERT_FILLED",
}

/**
 * A value used to tint the color of various UI components.
 * If not set, the client will use a fallback color.
 */
export enum PaymentHistoryDataUiStatusTreatment {
  /** CUSTOMER - UI components are tinted with the remote UiCustomer.accent_color. */
  CUSTOMER = "CUSTOMER",
  /** FAILED - UI components are tinted red. */
  FAILED = "FAILED",
  /** PENDING - UI components are tinted green (was previously blue). */
  PENDING = "PENDING",
  /** COMPLETED - UI components are tinted green. */
  COMPLETED = "COMPLETED",
  /** SECURITY - UI components are tinted security blue (different from PENDING). */
  SECURITY = "SECURITY",
  /** NONE - UI components should not be tinted. Introduced in 3.20. */
  NONE = "NONE",
  /** GREY - UI components are tinted grey. */
  GREY = "GREY",
}

/** Icons shown in the list view. Added in 3.13. */
export enum PaymentHistoryDataOverlayIcon {
  /** RECURRING_OVERLAY - Looks like an arrow that loops around and points at its tail. Added in 3.13 */
  RECURRING_OVERLAY = "RECURRING_OVERLAY",
  /** LOYALTY_OVERLAY - A star that indicates if a payment was associated with a loyalty account. */
  LOYALTY_OVERLAY = "LOYALTY_OVERLAY",
  /**
   * LINKED_ACCOUNT - Two connected chain links that indicates a cashtag linked to a merchant store was used
   * for the payment. Added in 3.46.
   */
  LINKED_ACCOUNT = "LINKED_ACCOUNT",
  /** LIGHTNING_BOLT - Lightning bolt icon. Indicates a bitcoin withdrawal using lightning. * */
  LIGHTNING_BOLT = "LIGHTNING_BOLT",
  /**
   * ROUND_UP - Round up icon. Looks like an arrow that points up with a curly tail on the left. On an equity or bitcoin,
   * it indicates that the order originated from the 'round-up' automated investment feature.
   */
  ROUND_UP = "ROUND_UP",
  /**
   * INVEST_WHERE_YOU_SHOP - FINANCIAL SERVICES HACKWEEK 2022 Q3 - plz remove!
   * Invest Where You Shop icon.
   */
  INVEST_WHERE_YOU_SHOP = "INVEST_WHERE_YOU_SHOP",
  /** INSTANT_PAY_LIGHTNING_BOLT - Blue lightning bolt icon used for the Instant Pay Product. * */
  INSTANT_PAY_LIGHTNING_BOLT = "INSTANT_PAY_LIGHTNING_BOLT",
  /** COUPON_OVERLAY - A coupon icon that indicates a coupon was received/redeemed/expired. */
  COUPON_OVERLAY = "COUPON_OVERLAY",
  /** OVERDRAFT_PROTECTION - A dashed circle with a checkmark inside. Indicates if a payment used overdraft protection. Added in 3.88. */
  OVERDRAFT_PROTECTION = "OVERDRAFT_PROTECTION",
  /** PAYMENT_PLAN_OVERLAY - Split arrows. Indicates a Payment Plan. Added in 3.91. */
  PAYMENT_PLAN_OVERLAY = "PAYMENT_PLAN_OVERLAY",
}

/** Icon overlay shape shown in the list view. Added in 3.56. */
export enum PaymentHistoryDataIconOverlayShape {
  /** CIRCULAR_SHAPE - Looks like a circle that overlays customer avatar. */
  CIRCULAR_SHAPE = "CIRCULAR_SHAPE",
  /** CARD_SHAPE - Looks like a card that overlays as a badge on the customer avatar. */
  CARD_SHAPE = "CARD_SHAPE",
  /** CIRCULAR_BADGE_SHAPE - Looks like a circle that overlays as a badge on the customer avatar. */
  CIRCULAR_BADGE_SHAPE = "CIRCULAR_BADGE_SHAPE",
  /** SQUARE_NO_CUTOUT - Looks like a square that overlays the avatar without cutouts */
  SQUARE_NO_CUTOUT = "SQUARE_NO_CUTOUT",
}

/** Describes an inline button shown in the flat activity list. */
export interface PaymentHistoryDataInlinePaymentHistoryButton {
  button?: PaymentHistoryButton;
  button_treatment?: PaymentHistoryDataInlineButtonTreatment;
}

/** Data for overriding the inline button on threaded views */
export interface PaymentHistoryDataThreadedButtonOverride {
  /** Whether the button should be hidden or not. If true, 'button' field is ignored */
  hide?: boolean;
  /** The alternative button to show in case it's not hidden */
  button?: PaymentHistoryDataInlinePaymentHistoryButton;
}

/** A named payment attribute to be presented above the detail_rows. */
export interface PaymentHistoryDataCalloutDetailItem {
  label?: string;
  value?: string;
}

export interface PaymentHistoryDataDetailRow {
  label?: string;
  value?: string;
  treatment?: PaymentHistoryDataDetailRowDetailRowTreatment;
}

export enum PaymentHistoryDataDetailRowDetailRowTreatment {
  /** NORMAL - Display the label and value in grey. */
  NORMAL = "NORMAL",
  /** STRIKETHROUGH - Display the label in grey, and the value in a lighter grey with a strikethrough across it. */
  STRIKETHROUGH = "STRIKETHROUGH",
  /** LABEL_INDENT - Display the label and value in grey, with a horizontal indentation applied to the position of the label. */
  LABEL_INDENT = "LABEL_INDENT",
  /** BOLD - Display the label and value in grey, with a bold font. */
  BOLD = "BOLD",
  /** TINT - Display the label and value with the merchant's tint color (or grey if not set). */
  TINT = "TINT",
  /** SEPARATOR - Display a horizontal separator row. No text values are displayed. */
  SEPARATOR = "SEPARATOR",
}

/** Text shown below the amount in the "more info sheet" detail view. Added for 2.20. */
export interface PaymentHistoryDataDetailsViewContent {
  rows?: string[];
}

export interface PaymentHistoryDataSupportOptions {
  /** e.g. “The quickest way to solve an issue with a card transaction is to contact the merchant” */
  header_text?: string;
  /** Buttons that get displayed in the support options modal, such as “Call the merchant”, “View Support Articles” */
  buttons?: PaymentHistoryButton[];
}

/** Represents an info module displayed in the receipt. Added in 3.20. */
export interface PaymentHistoryDataInfoModule {
  /** Title of the info module. Should never be null. */
  title?: string;
  /** Icon displayed above the info module title. May be null. */
  title_icon?: PaymentHistoryDataIcon;
  /** If set, overrides the tint of the title_icon. Added in 3.92. */
  title_icon_color?: Color;
  /** Info module description. This or description_markdown must not be null. */
  description?: string;
  /** Info module description, markdown formatted. This or description must not be null. */
  description_markdown?: string;
  /** Info module button. Can be null. Added in 3.41. */
  button?: PaymentHistoryButton;
}

export interface PaymentHistoryDataStackedAvatars {
  images?: Image[];
  overflow_count?: number;
  overflow_accent_color?: Color;
}

export interface PaymentHistoryDataAssociatedPaymentsOverride {
  subtitle?: string;
}

export interface PaymentHistoryReactions {
  available_reactions?: Reaction[];
  show_extended_picker?: boolean;
}

export interface PaymentHistoryButton {
  text?: string;
  action?: PaymentHistoryButtonButtonAction;
  /**
   * The URL to navigate to when the button is clicked.
   * Must be populated when button_action is OPEN_URL, VERIFY_IDENTITY, and REPORT_PROBLEM.
   */
  url?: string;
  /**
   * The number to call when the button is clicked.
   * Must be populated when button_action is CALL_NUMBER.
   */
  tel_number?: string;
  /** The client scenario to complete when action is COMPLETE_CLIENT_SCENARIO. */
  client_scenario?: ClientScenario;
  /** Icon to display in the button, beside the text. */
  icon?: PaymentHistoryButtonIcon;
  treatment?: PaymentHistoryDataUiStatusTreatment;
  /**
   * The node to begin at in the support flow.
   * Optional. May be populated when button_action is START_SUPPORT_FLOW.
   */
  support_flow_node?: string;
  /** Frequency for recurring purchase. Must be populated when button_action is CANCEL_RECURRING_PURCHASE */
  recurring_frequency?: RecurringScheduleFrequency;
  /**
   * An optional opaque string that gets sent back to the server in InitiateLoanPayment.opaque_data
   * when the button_action is MAKE_LOAN_PAYMENT. Other ButtonActions may use this in the future to
   * round trip data to the server via their respective APIs.
   */
  opaque_data?: string;
  dialog?: PaymentHistoryButtonDialog;
  share_sheet_details?: PaymentHistoryButtonShareSheetDetails;
}

export enum PaymentHistoryButtonButtonAction {
  /** CONFIRM - Confirms this payment (for an outstanding request or potentially duplicate payment). */
  CONFIRM = "CONFIRM",
  /** CANCEL - Revokes/Rejects/Retracts/Refuses this payment. */
  CANCEL = "CANCEL",
  /** PASSCODE_CONFIRMATION - Presents passcode confirmation for this payment. */
  PASSCODE_CONFIRMATION = "PASSCODE_CONFIRMATION",
  /** LINK_CARD - Presents card link flow for this payment. */
  LINK_CARD = "LINK_CARD",
  /** OPEN_URL - Open the URL in button_url. */
  OPEN_URL = "OPEN_URL",
  /** CALL_NUMBER - Call the number in button_tel_number. */
  CALL_NUMBER = "CALL_NUMBER",
  /** VERIFY_IDENTITY - Presents identify verification to the customer. */
  VERIFY_IDENTITY = "VERIFY_IDENTITY",
  /** REPORT_PROBLEM - Presents a web form to report a problem with this payment. */
  REPORT_PROBLEM = "REPORT_PROBLEM",
  /** COMPLETE_SCENARIO_PLAN - Present a screen based on the ScenarioPlan. Introduced in v2.5 of the app. */
  COMPLETE_SCENARIO_PLAN = "COMPLETE_SCENARIO_PLAN",
  /** REFUND - Initiates a refund of this payment. Introduced in v2.7 of the app. */
  REFUND = "REFUND",
  /** REPORT_ABUSE - Blocks the payment (if its blockable) and reports the user as abusive. */
  REPORT_ABUSE = "REPORT_ABUSE",
  /** UNREPORT_ABUSE - Unblocks the user. */
  UNREPORT_ABUSE = "UNREPORT_ABUSE",
  /** COMPLETE_CLIENT_SCENARIO - Action to complete a client_scenario. */
  COMPLETE_CLIENT_SCENARIO = "COMPLETE_CLIENT_SCENARIO",
  /** SHOW_MORE_INFO_SHEET - Action to show the more info sheet (from the detail view). */
  SHOW_MORE_INFO_SHEET = "SHOW_MORE_INFO_SHEET",
  /** CHECK_STATUS - Action to check the status of a payment. Introduced in v2.33 of the app. */
  CHECK_STATUS = "CHECK_STATUS",
  /** ADD_REACTION - Action to add a reaction to a payment. Introduced in v2.40 of the app. */
  ADD_REACTION = "ADD_REACTION",
  /** BITCOIN_DEPOSIT_REVERSAL - Action to initiate reversing a rejected Bitcoin deposit to an external wallet address. */
  BITCOIN_DEPOSIT_REVERSAL = "BITCOIN_DEPOSIT_REVERSAL",
  /** START_SUPPORT_FLOW - Action to start a support flow at the provided node. */
  START_SUPPORT_FLOW = "START_SUPPORT_FLOW",
  /** PAY_WITH_CASH_REDIRECT - Action to redirect to the platform configured external URL. Used for PAY_WITH_CASH. */
  PAY_WITH_CASH_REDIRECT = "PAY_WITH_CASH_REDIRECT",
  /** CANCEL_INVESTMENT_ORDER - Action to cancel scheduled investment orders. */
  CANCEL_INVESTMENT_ORDER = "CANCEL_INVESTMENT_ORDER",
  /** MAKE_LOAN_PAYMENT - Action to create a loan payment. */
  MAKE_LOAN_PAYMENT = "MAKE_LOAN_PAYMENT",
  /** SKIP_LOAN_PAYMENT - Action to skip a loan payment. */
  SKIP_LOAN_PAYMENT = "SKIP_LOAN_PAYMENT",
  /** CANCEL_RECURRING_PURCHASE - Cancel a recurring purchase. */
  CANCEL_RECURRING_PURCHASE = "CANCEL_RECURRING_PURCHASE",
  /** SHOW_SUPPORT_OPTIONS - Action to show support options (from the more info sheet) */
  SHOW_SUPPORT_OPTIONS = "SHOW_SUPPORT_OPTIONS",
  /** SHOW_PAYMENT_DETAIL_VIEW - Action to show the payment detail view */
  SHOW_PAYMENT_DETAIL_VIEW = "SHOW_PAYMENT_DETAIL_VIEW",
  /** ACCEPT_CRYPTO_PAYMENT - Action to accept a pending crypto payment */
  ACCEPT_CRYPTO_PAYMENT = "ACCEPT_CRYPTO_PAYMENT",
  /**
   * ACCEPT_INVEST_PAYMENT - Action to accept a pending invest payment (https://go/investp2p). Deprecated because we
   * replaced it with `OPEN_URL` and a client route.
   *
   * @deprecated
   */
  ACCEPT_INVEST_PAYMENT = "ACCEPT_INVEST_PAYMENT",
  /**
   * VIEW_PROFILE - Action to view the counterpart's profile. Deprecated: use the 'View Customer Profile' client route instead.
   *
   * @deprecated
   */
  VIEW_PROFILE = "VIEW_PROFILE",
  /**
   * CANCEL_CRYPTO_ORDER - Action to cancel a crypto <> fiat exchange order.
   * Only applicable to custom orders as of Apr 2021.
   */
  CANCEL_CRYPTO_ORDER = "CANCEL_CRYPTO_ORDER",
  SHOW_BNPL_LOAN = "SHOW_BNPL_LOAN",
  /**
   * OPEN_DIALOG - Shows a read-only dialog which displays the button's `dialog` within it. The dialog is to be
   * closed if the customer taps on the dialog's button or outside of the dialog. Displaying or
   * dismissing the dialog should not have side-effects.
   */
  OPEN_DIALOG = "OPEN_DIALOG",
  /** OPEN_SHARE_SHEET - Action to open share sheet with corresponding content details */
  OPEN_SHARE_SHEET = "OPEN_SHARE_SHEET",
}

export enum PaymentHistoryButtonIcon {
  /** CHECKMARK - Checkmark icon. */
  CHECKMARK = "CHECKMARK",
  /** INSTANT - Instant lightning bolt icon. */
  INSTANT = "INSTANT",
  /** HEART_OUTLINE - Heart outline icon. Used for reactions. */
  HEART_OUTLINE = "HEART_OUTLINE",
  /** SHIELD - Shield icon. Used for payments awaiting Identity verification. */
  SHIELD = "SHIELD",
  /** DOUBLE_CHEVRON - >> Icon. Used for scheduled payments that have been skipped. */
  DOUBLE_CHEVRON = "DOUBLE_CHEVRON",
  /** BLOCKED - A circle with a line through it. Used for scheduled payments that have been missed. */
  BLOCKED = "BLOCKED",
  /** SPARKLES - Icon with stars/sparkles. Used to indicate that the payment has a personalization on it. */
  SPARKLES = "SPARKLES",
}

/** To be used with an `OPEN_DIALOG` action. */
export interface PaymentHistoryButtonDialog {
  /** If empty, the dialog is to be displayed without a title. */
  title?: string;
  content?: string;
  dismiss_button_label?: string;
}

/** To be used with an `OPEN_SHARE_SHEET` action. */
export interface PaymentHistoryButtonShareSheetDetails {
  /** If empty, the share sheet is to be displayed without a title. */
  title?: string;
  default_phone_number?: string;
  content?: string;
}

export interface ScenarioPlanMap {
  /**
   * A map of keys to ScenarioPlans. Each entry represents a different action that the customer
   * may initiate. See ScenarioPlanEntry.Key for the set of possible actions. An updated ScenarioPlan
   * is returned in the responses to resolve each blocker in the ScenarioPlans.
   */
  scenario_plans?: ScenarioPlanEntry[];
}

export interface ScenarioPlanEntry {
  client_scenario?: ClientScenario;
  scenario_plan?: ScenarioPlan;
}

/**
 * Replies come in either the form of an image or a reaction. These replies are
 * threaded to a payment and ordered by created_at.
 */
export interface ThreadedPaymentReply {
  created_at?: number;
  reaction?: string;
  image_url?: string;
  author_customer_token?: string;
}

/** The event used when replying to a payment. */
export interface ThreadedPaymentReplyEvent {
  payment_token?: string;
  reply?: ThreadedPaymentReply;
}
